import type { HelpSidebarModel } from '.';
import { helpGuideById } from '../content/guides';
import type { HelpDocumentId, HelpGuideId, HelpLocation } from '../content/types';
import type { HelpAssistantPanelAction } from './state/actions';
import { HelpAssistantPanelStateModel } from './state/model';
import { clampPanelWidth, DEFAULT_PANEL_WIDTH } from './state/types';
import type { HelpAssistantPanelState } from './state/types';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { inject, injectable } from 'src/features/ioc';

const STORAGE_KEY = 'helpSidebar.location';
const STORAGE_KEY_OPEN = 'helpSidebar.isOpen';
const STORAGE_KEY_PANEL_OPEN = 'helpAssistantPanel.isOpen';
const STORAGE_KEY_ACTIVE_MODE = 'helpAssistantPanel.activeMode';
const STORAGE_KEY_PANEL_WIDTH = 'helpAssistantPanel.panelWidth';
const DEFAULT_LOCATION: HelpLocation = { type: 'browse', path: [] };

function loadLocation(): HelpLocation {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return DEFAULT_LOCATION;
        const parsed = JSON.parse(stored) as HelpLocation;
        if (parsed.type === 'contact') return DEFAULT_LOCATION;
        if (parsed.type === 'guide') return DEFAULT_LOCATION;
        if (parsed.type === 'document' && typeof parsed.documentId === 'string') return parsed;
        if (parsed.type === 'browse' && Array.isArray(parsed.path)) return parsed;
        return DEFAULT_LOCATION;
    } catch {
        return DEFAULT_LOCATION;
    }
}

function saveLocation(location: HelpLocation): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(location));
    } catch {
        // Ignore storage errors
    }
}

function loadOpenState(): boolean {
    try {
        const panelOpen = localStorage.getItem(STORAGE_KEY_PANEL_OPEN);
        if (panelOpen === 'true') return true;
        if (panelOpen === 'false') return false;
        return localStorage.getItem(STORAGE_KEY_OPEN) === 'true';
    } catch {
        return false;
    }
}

function saveOpenState(isOpen: boolean): void {
    try {
        localStorage.setItem(STORAGE_KEY_PANEL_OPEN, String(isOpen));
        localStorage.setItem(STORAGE_KEY_OPEN, String(isOpen));
    } catch {
        // Ignore storage errors
    }
}

function loadActiveMode(): HelpAssistantPanelState['activeMode'] {
    try {
        const activeMode = localStorage.getItem(STORAGE_KEY_ACTIVE_MODE);
        return activeMode === 'assistant' ? 'assistant' : 'help';
    } catch {
        return 'help';
    }
}

function saveActiveMode(activeMode: HelpAssistantPanelState['activeMode']): void {
    try {
        localStorage.setItem(STORAGE_KEY_ACTIVE_MODE, activeMode);
    } catch {
        // Ignore storage errors
    }
}

function loadPanelWidth(): number {
    try {
        const raw = localStorage.getItem(STORAGE_KEY_PANEL_WIDTH);
        if (raw == null) return DEFAULT_PANEL_WIDTH;
        const parsed = Number(raw);
        if (!Number.isFinite(parsed)) return DEFAULT_PANEL_WIDTH;
        return clampPanelWidth(parsed);
    } catch {
        return DEFAULT_PANEL_WIDTH;
    }
}

function savePanelWidth(width: number): void {
    try {
        localStorage.setItem(STORAGE_KEY_PANEL_WIDTH, String(width));
    } catch {
        // Ignore storage errors
    }
}

@injectable()
export class HelpSidebarModelImpl implements HelpSidebarModel {
    readonly #logger: Logger;
    readonly #state: HelpAssistantPanelStateModel;

    constructor(@inject('Logger') logger: Logger) {
        this.#logger = logger.getSubLogger({ name: 'HelpSidebarModel' });
        const initialLocation = loadLocation();
        const initialActiveMode = loadActiveMode();
        const initialPanelWidth = loadPanelWidth();
        if (TRACE) {
            this.#logger.trace('Loaded initial state', {
                location: initialLocation,
                activeMode: initialActiveMode,
                panelWidth: initialPanelWidth,
            });
        }
        this.#state = new HelpAssistantPanelStateModel({
            isOpen: loadOpenState(),
            activeMode: initialActiveMode,
            helpLocation: initialLocation,
            panelWidth: initialPanelWidth,
        });
    }

    get isOpen() {
        return this.#state.isOpen;
    }

    get activeMode() {
        return this.#state.activeMode;
    }

    get isHelpOpen() {
        return this.#state.isHelpOpen;
    }

    get isAssistantOpen() {
        return this.#state.isAssistantOpen;
    }

    get location() {
        return this.#state.location;
    }

    get hoveredStepIndex() {
        return this.#state.hoveredStepIndex;
    }

    get guideCompact() {
        return this.#state.guideCompact;
    }

    get panelWidth() {
        return this.#state.panelWidth;
    }

    #dispatch(action: HelpAssistantPanelAction): void {
        const previous = this.#state.state.value;
        const next = this.#state.dispatch(action);
        if (previous === next) return;

        saveOpenState(next.isOpen);
        saveActiveMode(next.activeMode);

        if (
            previous.helpLocation !== next.helpLocation &&
            next.helpLocation.type !== 'contact' &&
            next.helpLocation.type !== 'guide'
        ) {
            saveLocation(next.helpLocation);
        }

        if (previous.panelWidth !== next.panelWidth) {
            savePanelWidth(next.panelWidth);
        }
    }

    open(documentId?: HelpDocumentId): void {
        this.#dispatch({ type: 'OPEN_HELP', ...(documentId ? { documentId } : {}) });
        if (TRACE) {
            this.#logger.trace('Opened help sidebar', { location: this.#state.location.value });
        }
    }

    openAssistant(): void {
        this.#dispatch({ type: 'OPEN_ASSISTANT' });
        if (TRACE) {
            this.#logger.trace('Opened assistant panel');
        }
    }

    /**
     * Open the panel at its previously visited state (mode + location). If the
     * panel has never been opened before, default to the Assistant (Ask) tab.
     */
    resumePanel(): void {
        this.#dispatch({ type: 'RESUME_PANEL' });
        if (TRACE) {
            this.#logger.trace('Resumed help/assistant panel', {
                mode: this.#state.activeMode.value,
                location: this.#state.location.value,
            });
        }
    }

    setAssistantOpen(isOpen: boolean): void {
        if (isOpen) {
            this.openAssistant();
        } else {
            this.close();
        }
    }

    close(): void {
        this.#dispatch({ type: 'CLOSE_PANEL' });
        if (TRACE) {
            this.#logger.trace('Closed help and assistant panel');
        }
    }

    navigateToDocument(documentId: HelpDocumentId): void {
        this.#dispatch({ type: 'NAVIGATE_TO_DOCUMENT', documentId });
        if (TRACE) {
            this.#logger.trace('Navigated to document', { documentId });
        }
    }

    navigateToPath(path: string[]): void {
        this.#dispatch({ type: 'NAVIGATE_TO_PATH', path });
        if (TRACE) {
            this.#logger.trace('Navigated to path', { path });
        }
    }

    navigateToContact(): void {
        this.#dispatch({ type: 'NAVIGATE_TO_CONTACT' });
        if (TRACE) {
            this.#logger.trace('Navigated to contact form');
        }
    }

    startGuide(guideId: HelpGuideId): void {
        const guide = helpGuideById.get(guideId);
        if (!guide) return;
        this.#dispatch({ type: 'START_GUIDE', guideId });
        if (TRACE) {
            this.#logger.trace('Started guide', { guideId });
        }
    }

    setGuideStep(stepIndex: number): void {
        const loc = this.#state.location.value;
        if (loc.type !== 'guide') return;
        const guide = helpGuideById.get(loc.guideId);
        if (!guide || !Number.isInteger(stepIndex) || stepIndex < 0 || stepIndex >= guide.steps.length) return;
        this.#dispatch({ type: 'SET_GUIDE_STEP', stepIndex, guideStepCount: guide.steps.length });
        if (TRACE) {
            this.#logger.trace('Set guide step', { guideId: loc.guideId, stepIndex });
        }
    }

    setHoveredStep(stepIndex: number | null): void {
        let guideStepCount: number | undefined;
        if (stepIndex !== null) {
            const loc = this.#state.location.value;
            if (loc.type !== 'guide') return;
            const guide = helpGuideById.get(loc.guideId);
            if (!guide || !Number.isInteger(stepIndex) || stepIndex < 0 || stepIndex >= guide.steps.length) return;
            guideStepCount = guide.steps.length;
        }
        this.#dispatch({ type: 'SET_HOVERED_STEP', stepIndex, ...(guideStepCount ? { guideStepCount } : {}) });
    }

    setGuideCompact(compact: boolean): void {
        this.#dispatch({ type: 'SET_GUIDE_COMPACT', compact });
    }

    exitGuide(): void {
        this.#dispatch({ type: 'EXIT_GUIDE' });
        if (TRACE) {
            this.#logger.trace('Exited guide');
        }
    }

    setPanelWidth(width: number): void {
        this.#dispatch({ type: 'SET_PANEL_WIDTH', width });
        if (TRACE) {
            this.#logger.trace('Set panel width', { width: this.#state.panelWidth.value });
        }
    }
}
